import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import redditApi from '../../../services/redditApi';
import { useAccountQualityCheck, AccountQualityResult, NicheTags } from '../components/AccountQualityCheck';
import { NicheFitModal } from '../components/NicheFitModal';
import ArchiveToggle from '../components/ArchiveToggle';

const TERMINAL_STATUSES = new Set(['passed', 'failed', 'needs_manual_review', 'error']);

function rateColor(rate) {
  if (rate <= 10) return '#16a34a';
  if (rate <= 20) return '#ca8a04';
  return '#dc2626';
}

function riskBadge(rate) {
  if (rate >= 40) return <span className="rh-badge rh-badge-high">High</span>;
  if (rate >= 20) return <span className="rh-badge rh-badge-medium">Medium</span>;
  return <span className="rh-badge rh-badge-low">Low</span>;
}

function formatAccountAge(days) {
  if (days == null) return <span style={{ color: 'var(--rh-text-muted)' }}>—</span>;
  if (days < 30)  return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) {
    const m = Math.round(days / 30);
    return `${m} mo`;
  }
  const years = days / 365;
  return `${years.toFixed(1)} yr`;
}

// ── Shared modal overlay style ────────────────────────────────────────────────
const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};

function CloseIcon() {
  return (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" /><path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Delete Account Confirm Modal
// ─────────────────────────────────────────────────────────────────────────────
function DeleteAccountConfirmModal({ account, redditorId, onClose, onDeleted, onArchived }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const linked = (account.total ?? account.comment_count ?? 0) > 0;

  async function handleDelete() {
    setDeleting(true);
    setError('');
    try {
      if (linked) {
        await redditApi.post(
          `/admin/redditors/${redditorId}/accounts/${account.id}/archive/`,
          { archived: true },
        );
        onArchived?.();
      } else {
        await redditApi.delete(`/admin/redditors/${redditorId}/accounts/${account.id}/`);
        onDeleted();
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || (linked ? 'Failed to archive account.' : 'Failed to delete account.'));
      setDeleting(false);
    }
  }

  return (
    <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && !deleting && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 440, width: '95%' }}>
        <div className="rh-modal-header">
          <h3 className="rh-modal-title" style={{ color: linked ? '#b45309' : '#dc2626' }}>
            {linked ? 'Archive Account' : 'Delete Account'}
          </h3>
          <button className="rh-modal-close" onClick={onClose} disabled={deleting}><CloseIcon /></button>
        </div>
        <div className="rh-modal-body" style={{ padding: '20px 32px' }}>
          {linked ? (
            <>
              <p style={{ fontSize: 14, margin: '0 0 12px', color: 'var(--rh-text)' }}>
                <strong>u/{account.username}</strong> is linked to{' '}
                <strong>{account.total ?? account.comment_count}</strong> comment
                {(account.total ?? account.comment_count) !== 1 ? 's' : ''} and cannot be deleted.
              </p>
              <div style={{
                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                padding: '10px 14px', fontSize: 13, color: '#92400e', lineHeight: 1.5,
              }}>
                Archive it instead to hide it from active lists while preserving the account
                and comment history.
              </div>
            </>
          ) : (
            <p style={{ fontSize: 14, margin: '0 0 12px', color: 'var(--rh-text)' }}>
              Delete <strong>u/{account.username}</strong>? This cannot be undone.
            </p>
          )}
          {error && <p className="rh-input-error" style={{ marginTop: 10 }}>{error}</p>}
        </div>
        <div className="rh-modal-footer">
          <button className="rh-btn-secondary" onClick={onClose} disabled={deleting}>Cancel</button>
          <button
            type="button"
            className="rh-btn-secondary"
            style={{
              background: linked ? '#b45309' : '#dc2626',
              color: '#fff',
              border: 'none',
              boxShadow: linked
                ? '0 1px 3px rgba(180, 83, 9, 0.25)'
                : '0 1px 3px rgba(220, 38, 38, 0.25)',
            }}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting
              ? (linked ? 'Archiving…' : 'Deleting…')
              : (linked ? 'Archive Account' : 'Delete Account')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inline input style ────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  border: '1.5px solid var(--rh-border)', borderRadius: 8,
  padding: '9px 12px', fontSize: 13, color: 'var(--rh-text)',
  background: '#fff', outline: 'none', fontFamily: 'inherit',
};

const fieldLabel = {
  fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
};

// ─────────────────────────────────────────────────────────────────────────────
// Single Add Modal
// ─────────────────────────────────────────────────────────────────────────────
function SingleAddModal({ redditorId, onClose, onAdded }) {
  const qc = useAccountQualityCheck();
  const [username, setUsername] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  function handleClose() {
    qc.reset();
    onClose();
  }

  async function handleCheck(e) {
    e.preventDefault();
    if (!username.trim()) return;
    setAddError('');
    qc.validate(username.trim());
  }

  async function handleAdd(force = false) {
    if (!qc.result?.validation_id) return;
    setAdding(true);
    setAddError('');
    try {
      await redditApi.post(`/admin/redditors/${redditorId}/accounts/`, {
        validation_id: qc.result.validation_id,
        force,
      });
      onAdded();
      handleClose();
    } catch (err) {
      setAddError(err.response?.data?.detail || 'Failed to add account.');
    } finally {
      setAdding(false);
    }
  }

  const hasResult = qc.phase !== 'idle';

  return (
    <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="rh-modal" style={{
        maxWidth: hasResult ? 960 : 560,
        width: '95%',
        transition: 'max-width 0.25s ease',
      }}>
        <div className="rh-modal-header">
          <h3 className="rh-modal-title">Add Account</h3>
          <button className="rh-modal-close" onClick={handleClose}><CloseIcon /></button>
        </div>

        <div className="rh-modal-body" style={{
          padding: '24px 32px',
          display: hasResult ? 'grid' : 'flex',
          gridTemplateColumns: hasResult ? '260px 1fr' : undefined,
          flexDirection: hasResult ? undefined : 'column',
          gap: 28,
          alignItems: 'flex-start',
        }}>
          {/* Left: input */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <p style={fieldLabel}>Reddit Username</p>
              <form onSubmit={handleCheck} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  style={{ ...inputStyle }}
                  placeholder="username or u/username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={qc.phase === 'checking'}
                  autoFocus
                  onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--rh-border)'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  type="submit"
                  className="rh-btn rh-btn-primary"
                  disabled={!username.trim() || qc.phase === 'checking'}
                  style={{ width: '100%' }}
                >
                  {qc.phase === 'checking' ? 'Checking…' : 'Run Quality Check'}
                </button>
              </form>
              {qc.error && (
                <p style={{ fontSize: 12, color: '#dc2626', marginTop: 8, marginBottom: 0 }}>{qc.error}</p>
              )}
            </div>
            {hasResult && (
              <button
                className="rh-btn rh-btn-secondary"
                style={{ fontSize: 12, width: '100%' }}
                onClick={() => { qc.reset(); setUsername(''); setAddError(''); }}
              >
                Check a different username
              </button>
            )}
            {!hasResult && (
              <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', margin: 0, lineHeight: 1.5 }}>
                A quality check will verify account age, karma, activity, and participation before adding.
              </p>
            )}
          </div>

          {/* Right: quality check result */}
          {hasResult && (
            <div style={{ borderLeft: '1px solid var(--rh-border)', paddingLeft: 28 }}>
              <AccountQualityResult
                result={qc.result}
                adding={adding}
                addError={addError}
                onAddAccount={() => handleAdd(false)}
                onForceAdd={() => handleAdd(true)}
                onRetry={() => { qc.reset(); setAddError(''); }}
              />
            </div>
          )}
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn rh-btn-secondary" onClick={handleClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk Add Modal
// ─────────────────────────────────────────────────────────────────────────────
function BulkAddModal({ redditorId, onClose, onAdded }) {
  const [bulkInput, setBulkInput] = useState('');
  // items: { username, status: pending|checking|passed|failed|needs_manual_review|error, result, addState: idle|adding|added|error, addError }
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState('input'); // 'input' | 'checking' | 'results'
  const pollersRef = useRef({});
  const anyAdded = useRef(false);

  // Cleanup all pollers on unmount
  useEffect(() => {
    return () => {
      Object.values(pollersRef.current).forEach(clearInterval);
    };
  }, []);

  // Auto-advance to results when all checks are terminal
  useEffect(() => {
    if (phase !== 'checking' || items.length === 0) return;
    if (items.every(it => TERMINAL_STATUSES.has(it.status))) {
      setPhase('results');
    }
  }, [items, phase]);

  function parseUsernames(text) {
    return [...new Set(
      text.split(/[\n,]+/)
        .map(u => u.trim().replace(/^u\//i, '').replace(/^@/, ''))
        .filter(Boolean),
    )];
  }

  function stopPoller(idx) {
    if (pollersRef.current[idx]) {
      clearInterval(pollersRef.current[idx]);
      delete pollersRef.current[idx];
    }
  }

  async function startValidation(username, idx) {
    try {
      const { data } = await redditApi.post('/accounts/validate/', { username });
      setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, validationId: data.validation_id, status: 'checking' } : it,
      ));
      pollersRef.current[idx] = setInterval(async () => {
        try {
          const { data: pd } = await redditApi.get(`/accounts/validations/${data.validation_id}/`);
          setItems(prev => prev.map((it, i) => {
            if (i !== idx) return it;
            if (TERMINAL_STATUSES.has(pd.status)) stopPoller(idx);
            return { ...it, status: pd.status, result: pd };
          }));
        } catch { /* network blip */ }
      }, 3000);
    } catch (err) {
      setItems(prev => prev.map((it, i) =>
        i === idx
          ? { ...it, status: 'error', addError: err.response?.data?.detail || 'Could not start check.' }
          : it,
      ));
    }
  }

  function handleCheckAll() {
    const usernames = parseUsernames(bulkInput);
    if (!usernames.length) return;
    const initial = usernames.map(u => ({
      username: u, validationId: null, status: 'pending', result: null, addState: 'idle', addError: '',
    }));
    setItems(initial);
    setPhase('checking');
    usernames.forEach((u, i) => startValidation(u, i));
  }

  async function addAccount(idx, force = false) {
    const item = items[idx];
    if (!item?.result?.validation_id) return;
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, addState: 'adding', addError: '' } : it,
    ));
    try {
      await redditApi.post(`/admin/redditors/${redditorId}/accounts/`, {
        validation_id: item.result.validation_id,
        force,
      });
      anyAdded.current = true;
      setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, addState: 'added' } : it,
      ));
    } catch (err) {
      setItems(prev => prev.map((it, i) =>
        i === idx
          ? { ...it, addState: 'error', addError: err.response?.data?.detail || 'Failed to add.' }
          : it,
      ));
    }
  }

  async function addAllPassed() {
    const toAdd = items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => it.status === 'passed' && it.addState === 'idle');
    for (const { i } of toAdd) {
      await addAccount(i, false);
    }
  }

  function handleClose() {
    Object.values(pollersRef.current).forEach(clearInterval);
    if (anyAdded.current) onAdded();
    onClose();
  }

  function handleReset() {
    Object.values(pollersRef.current).forEach(clearInterval);
    pollersRef.current = {};
    anyAdded.current = false;
    setItems([]);
    setPhase('input');
    setBulkInput('');
  }

  const checkedCount = items.filter(it => TERMINAL_STATUSES.has(it.status)).length;
  const passedCount = items.filter(it => it.status === 'passed').length;
  const addableCount = items.filter(it => it.status === 'passed' && it.addState === 'idle').length;

  // Status badge for bulk table rows
  function BulkStatusBadge({ status }) {
    const map = {
      pending:             { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb', label: 'Pending' },
      checking:            { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', label: 'Checking…' },
      passed:              { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', label: 'Passed' },
      failed:              { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'Failed' },
      needs_manual_review: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', label: 'Needs Review' },
      error:               { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'Error' },
    };
    const s = map[status] || map.pending;
    return (
      <span style={{
        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}>
        {status === 'checking' && (
          <span className="rh-qg-spinner" style={{ width: 9, height: 9, borderWidth: 1.5 }} />
        )}
        {s.label}
      </span>
    );
  }

  return (
    <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="rh-modal" style={{ maxWidth: 720, width: '95%' }}>
        <div className="rh-modal-header">
          <h3 className="rh-modal-title">Bulk Add Accounts</h3>
          <button className="rh-modal-close" onClick={handleClose}><CloseIcon /></button>
        </div>

        <div className="rh-modal-body" style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Phase: input */}
          {phase === 'input' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <p style={fieldLabel}>Reddit Usernames</p>
                <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', margin: '0 0 8px' }}>
                  One username per line, or comma-separated. The "u/" prefix is stripped automatically.
                </p>
                <textarea
                  style={{
                    ...inputStyle, resize: 'vertical', minHeight: 120,
                    lineHeight: 1.65,
                  }}
                  placeholder={"username1\nusername2\nu/username3"}
                  value={bulkInput}
                  onChange={(e) => setBulkInput(e.target.value)}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--rh-border)'; e.target.style.boxShadow = 'none'; }}
                  autoFocus
                />
                <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', margin: '6px 0 0' }}>
                  {parseUsernames(bulkInput).length} username{parseUsernames(bulkInput).length !== 1 ? 's' : ''} detected
                </p>
              </div>
            </div>
          )}

          {/* Phase: checking + results — show table */}
          {(phase === 'checking' || phase === 'results') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Progress / summary bar */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'var(--rh-surface)', border: '1px solid var(--rh-border)',
                borderRadius: 8, padding: '10px 14px',
              }}>
                <span style={{ fontSize: 13, color: 'var(--rh-text)', fontWeight: 600 }}>
                  {phase === 'checking'
                    ? `Checking… ${checkedCount} / ${items.length}`
                    : `${items.length} account${items.length !== 1 ? 's' : ''} checked — ${passedCount} passed`}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {phase === 'results' && addableCount > 0 && (
                    <button
                      className="rh-btn rh-btn-primary"
                      style={{ fontSize: 12, padding: '6px 14px' }}
                      onClick={addAllPassed}
                    >
                      Add All Passed ({addableCount})
                    </button>
                  )}
                  <button
                    className="rh-btn rh-btn-secondary"
                    style={{ fontSize: 12, padding: '6px 14px' }}
                    onClick={handleReset}
                  >
                    Start over
                  </button>
                </div>
              </div>

              {/* Results table */}
              <div className="rh-table-wrap" style={{ maxHeight: 380, overflowY: 'auto' }}>
                <table className="rh-table" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '40%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '35%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={item.username}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                          u/{item.username}
                        </td>
                        <td>
                          <BulkStatusBadge status={item.status} />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {item.addState === 'added' && (
                            <span style={{ fontSize: 12, color: '#15803d', fontWeight: 700 }}>
                              ✓ Added
                            </span>
                          )}
                          {item.addState === 'adding' && (
                            <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>Adding…</span>
                          )}
                          {item.addState === 'error' && (
                            <span style={{ fontSize: 11, color: '#dc2626' }}>{item.addError}</span>
                          )}
                          {item.addState === 'idle' && (
                            <div style={{ display: 'inline-flex', gap: 6 }}>
                              {item.status === 'passed' && (
                                <button
                                  className="rh-btn rh-btn-primary"
                                  style={{ fontSize: 11, padding: '4px 10px' }}
                                  onClick={() => addAccount(idx, false)}
                                >
                                  Add
                                </button>
                              )}
                              {(item.status === 'failed' || item.status === 'needs_manual_review') && (
                                <button
                                  className="rh-btn-force"
                                  style={{ fontSize: 11, padding: '4px 10px' }}
                                  onClick={() => addAccount(idx, true)}
                                >
                                  Force Add
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn rh-btn-secondary" onClick={handleClose}>
            {anyAdded.current ? 'Done' : 'Cancel'}
          </button>
          {phase === 'input' && (
            <button
              className="rh-btn rh-btn-primary"
              disabled={!parseUsernames(bulkInput).length}
              onClick={handleCheckAll}
            >
              Check All ({parseUsernames(bulkInput).length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function RedditorDetailPage() {
  const { redditorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overdueHistory, setOverdueHistory] = useState(null);
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [overdueLoaded, setOverdueLoaded] = useState(false);

  const [showSingleAdd, setShowSingleAdd] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [fitModalAccount, setFitModalAccount] = useState(null);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState(null);

  // Tab state + sliding pill indicator
  const [detailTab, setDetailTab] = useState('accounts');
  const pillContainerRef = useRef(null);
  const pillRefs = useRef({});
  const [pillIndicator, setPillIndicator] = useState({ left: 0, width: 0, opacity: 0 });

  // Show archived accounts toggle (off by default)
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);

  const loadStats = useCallback(() => {
    const params = new URLSearchParams();
    if (showArchived) params.set('include_archived', 'true');
    const qs = params.toString();
    redditApi.get(`/admin/redditors/${redditorId}/accounts/stats/${qs ? `?${qs}` : ''}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setError('Failed to load redditor details.'))
      .finally(() => setLoading(false));
  }, [redditorId, showArchived]);

  function openFitModal(account) {
    setFitModalAccount(account);
    if (account?.needs_self_add_review && account.id) {
      redditApi.post(`/admin/accounts/${account.id}/self-added-review/`)
        .then(() => {
          setData((prev) => {
            if (!prev?.accounts) return prev;
            return {
              ...prev,
              accounts: prev.accounts.map((row) => (
                String(row.id) === String(account.id)
                  ? { ...row, needs_self_add_review: false, self_added_reviewed_at: new Date().toISOString() }
                  : row
              )),
            };
          });
        })
        .catch(() => {});
    }
  }

  const loadOverdue = useCallback(() => {
    setOverdueLoading(true);
    redditApi.get(`/admin/redditors/${redditorId}/overdue-history/`)
      .then(({ data: d }) => setOverdueHistory(d))
      .catch(() => setOverdueHistory(null))
      .finally(() => { setOverdueLoading(false); setOverdueLoaded(true); });
  }, [redditorId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Lazy-load overdue history only when the Overdue History tab is first opened
  useEffect(() => {
    if (detailTab === 'overdue' && !overdueLoaded && !overdueLoading) {
      loadOverdue();
    }
  }, [detailTab, overdueLoaded, overdueLoading, loadOverdue]);

  // Sliding pill indicator
  useEffect(() => {
    const btn = pillRefs.current[detailTab];
    const container = pillContainerRef.current;
    if (!btn || !container) return;
    const containerLeft = container.getBoundingClientRect().left;
    const btnRect = btn.getBoundingClientRect();
    setPillIndicator({ left: btnRect.left - containerLeft, width: btnRect.width, opacity: 1 });
  }, [detailTab, loading]);

  function handleAdded() {
    loadStats();
  }

  async function toggleArchive(account) {
    if (archivingId) return;
    const archived = !account.is_archived;
    setArchivingId(account.id);
    try {
      await redditApi.post(
        `/admin/redditors/${redditorId}/accounts/${account.id}/archive/`,
        { archived },
      );
      await loadStats();
    } catch {
      setError(`Failed to ${archived ? 'archive' : 'unarchive'} account.`);
    } finally {
      setArchivingId(null);
    }
  }

  // Shared "Add" buttons rendered in card headers
  function AddButtons() {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="rh-btn rh-btn-secondary"
          style={{ fontSize: 12, padding: '5px 12px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          onClick={() => setShowSingleAdd(true)}
        >
          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Account
        </button>
        <button
          className="rh-btn rh-btn-secondary"
          style={{ fontSize: 12, padding: '5px 12px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          onClick={() => setShowBulkAdd(true)}
        >
          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Bulk Add
        </button>
      </div>
    );
  }

  return (
    <div className="rh-redditor-detail-root" style={{ padding: '28px 32px' }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: '1px solid var(--rh-border)', borderRadius: 8,
            padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--rh-text)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--rh-text)' }}>
            {loading ? 'Loading…' : data?.redditor_name || 'Redditor'}
          </h2>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)', marginTop: 2 }}>
            Per-account performance breakdown
          </p>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '12px 18px', color: '#dc2626', marginBottom: 24,
        }}>
          {error}
        </div>
      )}

      {/* Tab pills */}
      <div className="rh-tab-pills" ref={pillContainerRef} style={{ marginBottom: 20 }}>
        <div
          className="rh-tab-pill-indicator"
          style={{
            left: pillIndicator.left,
            width: pillIndicator.width,
            opacity: pillIndicator.opacity,
          }}
        />
        {[
          { key: 'accounts', label: 'Reddit Accounts' },
          { key: 'deletion', label: 'Deletion Rate by Account' },
          { key: 'overdue', label: 'Overdue History' },
        ].map((t) => (
          <button
            key={t.key}
            ref={(el) => { pillRefs.current[t.key] = el; }}
            className={`rh-tab-pill${detailTab === t.key ? ' active' : ''}`}
            onClick={() => setDetailTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--rh-text-muted)', fontSize: 14 }}>
          Loading…
        </div>
      ) : data && (
        <>
          {detailTab === 'accounts' && (
          <>
          <div className="rh-card">
            <div className="rh-card-header">
              <h3 className="rh-card-title">Reddit Accounts</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>
                  {data.accounts.length} account{data.accounts.length !== 1 ? 's' : ''}
                </span>
                <ArchiveToggle
                  checked={showArchived}
                  onChange={setShowArchived}
                  onLabel="Hide archived"
                  offLabel="Show archived"
                  title="Show archived (soft-hidden) accounts alongside active ones."
                />
                <AddButtons />
              </div>
            </div>
          {data.accounts.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p style={{ color: 'var(--rh-text-muted)', fontSize: 14 }}>
                {showArchived
                  ? 'No Reddit accounts registered for this redditor yet.'
                  : 'No active Reddit accounts for this redditor. Turn on “Show archived” to reveal archived accounts.'}
              </p>
            </div>
          ) : (
            <div className="rh-table-wrap">
                <table className="rh-table">
                  <thead>
                    <tr>
                      <th>Reddit Account</th>
                      <th>Added by</th>
                      <th>Niche</th>
                      <th>Comment Karma</th>
                      <th>Account Age</th>
                      <th>Comments Submitted</th>
                      <th>Deleted</th>
                      <th>Deletion Rate</th>
                      <th>Risk Level</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((a) => (
                      <tr
                        key={a.id}
                        className="rh-account-row-clickable"
                        onClick={() => openFitModal(a)}
                        title="View client fit"
                      >
                        <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                          u/{a.username}
                          {a.is_banned && (
                            <span
                              className="rh-badge rh-badge-banned"
                              title={a.banned_at ? `Banned ${new Date(a.banned_at).toLocaleString()}` : 'Banned on Reddit'}
                              style={{ marginLeft: 6, fontFamily: 'inherit', fontSize: '0.68rem' }}
                            >
                              Banned
                            </span>
                          )}
                          {a.is_archived && (
                            <span
                              className="rh-badge rh-badge-archived"
                              title={a.archived_at ? `Archived ${new Date(a.archived_at).toLocaleString()}` : 'Archived'}
                              style={{ marginLeft: 6, fontFamily: 'inherit', fontSize: '0.68rem' }}
                            >
                              Archived
                            </span>
                          )}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                            {a.added_by === 'self' && (
                              <span
                                className="rh-badge rh-badge-self-added"
                                title="Added by the redditor from their dashboard"
                                style={{ fontFamily: 'inherit', fontSize: '0.68rem' }}
                              >
                                Self-added
                              </span>
                            )}
                            {a.needs_self_add_review && (
                              <span
                                className="rh-badge rh-badge-self-added-new"
                                title="Needs niche / client-fit review"
                                style={{ fontFamily: 'inherit', fontSize: '0.68rem' }}
                              >
                                New
                              </span>
                            )}
                            {a.added_by === 'admin' && (
                              <span
                                className="rh-badge rh-badge-admin-added"
                                title="Added by an admin"
                                style={{ fontFamily: 'inherit', fontSize: '0.68rem' }}
                              >
                                Admin-added
                              </span>
                            )}
                            {a.added_by === 'invite' && (
                              <span
                                className="rh-badge rh-badge-admin-added"
                                title="Added via invite"
                                style={{ fontFamily: 'inherit', fontSize: '0.68rem' }}
                              >
                                Invite
                              </span>
                            )}
                            {!a.added_by && (
                              <span style={{ color: 'var(--rh-text-muted)', fontSize: 12 }}>—</span>
                            )}
                          </span>
                        </td>
                        <td>
                          <NicheTags niches={a.niches} max={2} emptyText="—" />
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          {a.comment_karma != null
                            ? a.comment_karma.toLocaleString()
                            : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                        </td>
                        <td>{formatAccountAge(a.account_age_days)}</td>
                        <td style={{ color: '#16a34a', fontWeight: 500 }}>{a.submitted}</td>
                        <td style={{ color: '#dc2626' }}>{a.deleted}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: rateColor(a.deletion_rate) }}>
                            {a.deletion_rate}%
                          </span>
                        </td>
                        <td>{riskBadge(a.deletion_rate)}</td>
                        <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          <div className="rh-account-row-actions">
                            <button
                              className="rh-icon-btn"
                              title={a.is_archived ? 'Unarchive account' : 'Archive account (soft-hide, preserves data)'}
                              disabled={archivingId === a.id}
                              onClick={() => toggleArchive(a)}
                              style={{
                                opacity: archivingId === a.id ? 0.5 : 1,
                                color: a.is_archived ? 'var(--rh-text-muted)' : undefined,
                              }}
                            >
                              {a.is_archived ? (
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M3 12h18" /><path d="M12 3v18" />
                                </svg>
                              ) : (
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
                                </svg>
                              )}
                            </button>
                            <button
                              className="rh-icon-btn rh-icon-btn-danger"
                              title={(a.total || 0) > 0 ? 'Linked to comments — archive instead of delete' : 'Delete account'}
                              onClick={() => setDeleteAccountTarget(a)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          )}
            </div>
          </>
          )}

          {/* Bar chart */}
          {detailTab === 'deletion' && (
            <div className="rh-card" style={{ padding: '20px 24px', marginTop: 24 }}>
              <div className="rh-card-header" style={{ marginBottom: 20 }}>
                <h3 className="rh-card-title" style={{ margin: 0 }}>Deletion Rate by Account</h3>
                <ArchiveToggle
                  checked={showArchived}
                  onChange={setShowArchived}
                  onLabel="Hide archived"
                  offLabel="Show archived"
                  title="Show archived (soft-hidden) accounts alongside active ones."
                />
              </div>
              {data.accounts.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: 14 }}>
                  {showArchived
                    ? 'No Reddit accounts registered for this redditor yet.'
                    : 'No active Reddit accounts for this redditor. Turn on “Show archived” to reveal archived accounts.'}
                </div>
              ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {data.accounts.map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                      width: 140, fontSize: 13, fontFamily: 'monospace', fontWeight: 500,
                      color: 'var(--rh-text)', flexShrink: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      u/{a.username}
                    </span>
                    <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 6, height: 16, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, a.deletion_rate)}%`,
                        height: '100%',
                        background: rateColor(a.deletion_rate),
                        borderRadius: 6,
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <span style={{
                      width: 42, fontSize: 13, fontWeight: 600,
                      color: rateColor(a.deletion_rate), textAlign: 'right', flexShrink: 0,
                    }}>
                      {a.deletion_rate}%
                    </span>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Overdue History */}
      {detailTab === 'overdue' && (
      <div className="rh-card" style={{ marginTop: 24 }}>
        <div className="rh-card-header">
          <h3 className="rh-card-title">Overdue History</h3>
          {!overdueLoading && overdueHistory && (
            <span style={{
              fontSize: 12,
              color: overdueHistory.total_overdue_incidents === 0 ? '#15803d' : '#b91c1c',
              fontWeight: 600,
              background: overdueHistory.total_overdue_incidents === 0 ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${overdueHistory.total_overdue_incidents === 0 ? '#bbf7d0' : '#fecaca'}`,
              borderRadius: 20, padding: '2px 10px',
            }}>
              {overdueHistory.total_overdue_incidents} total incident{overdueHistory.total_overdue_incidents !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {overdueLoading ? (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: 14 }}>
            Loading…
          </div>
        ) : !overdueHistory ? (
          <div style={{ padding: '24px', color: 'var(--rh-text-muted)', fontSize: 14 }}>
            Could not load overdue history.
          </div>
        ) : overdueHistory.total_overdue_incidents === 0 ? (
          <div style={{
            padding: '32px 24px', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          }}>
            <svg width="32" height="32" fill="none" stroke="#16a34a" strokeWidth="1.5" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
            </svg>
            <p style={{ color: '#15803d', fontWeight: 600, fontSize: 14, margin: 0 }}>
              No overdue history — great track record!
            </p>
            <p style={{ color: 'var(--rh-text-muted)', fontSize: 12, margin: 0 }}>
              This redditor has never missed a posting deadline.
            </p>
          </div>
        ) : (
          <div className="rh-table-wrap">
            <table className="rh-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Post Title</th>
                  <th>Comment Date</th>
                  <th>Reminders Reached</th>
                  <th>Eventually Submitted?</th>
                </tr>
              </thead>
              <tbody>
                {overdueHistory.incidents.map((inc) => (
                  <tr key={inc.comment_id} style={{
                    background: inc.max_reminder_day >= 3 ? '#fff8f8' : 'transparent',
                  }}>
                    <td style={{ fontSize: 13 }}>{inc.client_name}</td>
                    <td style={{
                      fontSize: 12, color: 'var(--rh-text-muted)',
                      maxWidth: 260, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={inc.post_title}>
                      {inc.post_title}
                    </td>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {new Date(inc.publishing_date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </td>
                    <td>
                      <span style={{
                        fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '2px 8px',
                        ...(inc.max_reminder_day >= 3
                          ? { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }
                          : inc.max_reminder_day === 2
                          ? { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }
                          : { background: '#fefce8', color: '#92400e', border: '1px solid #fde68a' }),
                      }}>
                        Reminder {inc.max_reminder_day}
                        {inc.max_reminder_day >= 3 ? ' (CC\'d)' : ''}
                      </span>
                    </td>
                    <td>
                      {inc.eventually_submitted ? (
                        <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 13 }}>Yes</span>
                      ) : (
                        <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 13 }}>Still pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Single Add Modal */}
      {showSingleAdd && (
        <SingleAddModal
          redditorId={redditorId}
          onClose={() => setShowSingleAdd(false)}
          onAdded={handleAdded}
        />
      )}

      {/* Bulk Add Modal */}
      {showBulkAdd && (
        <BulkAddModal
          redditorId={redditorId}
          onClose={() => setShowBulkAdd(false)}
          onAdded={handleAdded}
        />
      )}

      {/* Niche ↔ Client Fit Modal */}
      {fitModalAccount && (
        <NicheFitModal
          account={fitModalAccount}
          onClose={() => setFitModalAccount(null)}
        />
      )}

      {/* Delete Account Confirm Modal */}
      {deleteAccountTarget && (
        <DeleteAccountConfirmModal
          account={deleteAccountTarget}
          redditorId={redditorId}
          onClose={() => setDeleteAccountTarget(null)}
          onDeleted={handleAdded}
          onArchived={handleAdded}
        />
      )}
    </div>
  );
}
